<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading || !isPhoneNumberValid"
    hide-reset
    loading-message="Pulling up customer info..."
    @submit="pullUpCustomerInfo"
  >
    <template #title>
      Pull Up Customer Info
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Phone Number </label>
        <phone-number-input
          v-model="phoneNumber"
          class="col-sm-6"
          :page-id="pageId"
        />
      </div>
    </template>

    <template #submit-button>
      Pull Up Customer Info
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { orderBy } from 'lodash';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';
import { CallerAddress } from '@/models/caller';
import { CustomLocationType } from '@/models/address';
import { trialInfo } from '@/services/abTests/abTestsViewModel';
import { callerService } from '@/services/caller';
import { planCurrency, planPricing } from '@/services/pricing';

const pageId = 'pullup-customer-info';

const emit = defineEmits(['goto-next-step']);

const { customerInfo, setNewCustomerInfo } = useCustomer();
const { showErrorPopupDialog } = useSwalDialog();
const { setRegistrationStartAddress } = useAddress();

const phoneNumber = ref('');
const isLoading = ref(false);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));

const getCustomAddresses = async (callerId: string) => {
  const response = await callerService.fetchCustomerAddress({
    callerId,
  });

  let customAddresses = response.rows.map((customLocation: CallerAddress, index: number) => ({
    ...customLocation,
    id: customLocation.id.toString(),
    index,
  }) as CustomLocationType);

  customAddresses = orderBy(customAddresses, ['order'], ['asc']);
  return customAddresses;
};

const pullUpCustomerInfo = async () => {
  isLoading.value = true;
  try {
    const response = await callerService.fetchCustomerInfo({
      phoneNumber: phoneNumber.value,
    });

    response.customAddresses = await getCustomAddresses(response.id);

    setNewCustomerInfo(response);
    setRegistrationStartAddress({
      streetAddress: response.streetAddress ?? '',
      streetAddress2: response.streetAddress2 ?? '',
      city: response.city ?? '',
      state: response.zip ?? '',
      zip: response.zip ?? '',
      country: response.country ?? '',
      latitude: response.latitude,
      longitude: response.longitude,
      category: 'home',
    });

    trialInfo.value = response.trialInfo ?? null;

    emit('goto-next-step');
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

watch(
  customerInfo,
  (newCustomer: any) => {
    phoneNumber.value = newCustomer.phoneNumber || '';
  },
  {
    immediate: true,
  },
);
</script>
